import { isTokenExpired, secondsUntilExpiry } from "@/utils/tokenUtils";
import type { JwtValidationResult } from "./types";
import { decodeJwt } from "./JwtDecoder";
import { safeLogError } from "@/services/DatadogService/safeLogger";

export { decodeJwt };

/**
 * Validates a token for format (decoding) and expiration.
 * Client-safe: does not pull in jsonwebtoken or crypto polyfills.
 * @param token The JWT token.
 * @returns The validation result.
 */
export const validateJwt = (token: string): JwtValidationResult => {
  if (!token) {
    return { isValid: false, error: "Token is required and must be a string" };
  }

  const payload = decodeJwt(token);
  if (!payload) {
    return { isValid: false, error: "Invalid token format" };
  }

  const currentTimeInSeconds = Math.floor(Date.now() / 1000);
  if (payload.exp && payload.exp < currentTimeInSeconds) {
    return { isValid: false, error: "Token has expired", payload };
  }

  return { isValid: true, payload };
};

/**
 * Checks if a token is expired.
 * Client-safe: does not pull in jsonwebtoken or crypto polyfills.
 * @param token The JWT token.
 * @returns True if the token is expired, false otherwise.
 */
export const isJwtExpired = (token: string): boolean => {
  try {
    if (!token) {
      return true;
    }

    const payload = decodeJwt(token);

    if (!payload?.exp) {
      return true;
    }

    return isTokenExpired(payload.exp);
  } catch (error) {
    safeLogError(error instanceof Error ? error : new Error(String(error)), {
      service: "JwtService",
      action: "isJwtExpired",
    });
    return true;
  }
};

/**
 * Returns seconds until token expiry.
 * Client-safe: does not pull in jsonwebtoken or crypto polyfills.
 * @param token The JWT token.
 * @returns The seconds until token expiry.
 */
export const getJwtTimeToExpiry = (token: string): number => {
  try {
    if (!token) {
      return 0;
    }

    const payload = decodeJwt(token);
    if (!payload?.exp) {
      return 0;
    }

    return secondsUntilExpiry(payload.exp);
  } catch (error) {
    safeLogError(error instanceof Error ? error : new Error(String(error)), {
      service: "JwtService",
      action: "getJwtTimeToExpiry",
    });
    return 0;
  }
};
